/**
 * Authors: mariushirjanu
 * Created: 08/10/2023
 */
import { Component, Input, OnInit } from '@angular/core';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { GaEventService } from '@app/shared/services/ga-event.service';
import { UrlService } from '@app/shared/browser/url.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { ScrollService } from '@app/shared/scroll/scroll.service';
import { SEC_WS_B2BSETTINGS } from '@app/shared/services/auth/security-objects';
import { TranslationService } from '@app/shared/translation/translation.service';
import { ResponsiveService } from '@app/shared/responsive/responsive.service';
import { GA1Service } from '@app/shared/ga1/ga1.service';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { IWebshopB2BSettingsData, WebshopB2BSettingsDataService } from './webshop-b2b-settings-data.service';
import { LOV_ORDER_PROCESS, LOV_PRICE_B2B_PO, LOV_PROCESS_NON_CATALOG_ORDERS, LovService } from '@app/shared/services/lov.service';
import { FaqLinksComponent } from '@app/shared/faq/faq-links.component';
import { FAQ_ARIBA_AGREEMENT, FAQ_B2B_BROKER_SSO, FAQ_B2B_CAPABILITIES, FAQ_CUSTOMER_B2B_FORM } from '@app/shared/faq/faq-codes';
import { ListContactsComponent } from '@app/shared/contact/list-contacts.component';
import { IContact } from '@type_definitions/datatypes';
import { ICollapsibleSection, ICollapsibleSectionFields } from '@app/shared/collapsible-sections/collapsible-sections.component';
import { GaStateService } from '@app/shared/services/ga-state.service';

@Component({
  selector: 'webshop-b2b-settings',
  template: `
    <collapsible-sections [formSections]="formSections" [model]="model" [loading]="loading"></collapsible-sections>
  `
})
export class WebshopB2BSettingsComponent implements OnInit {
  @Input() public webshopId:number;

  public formSections:ICollapsibleSection[];
  public model:IWebshopB2BSettingsData;
  public loading:boolean;

  private hasWritePermission:boolean;
  private labelClass:string = 'col-md-5';
  private valueClass:string = 'col-md-7';

  private priceSentInXmlFilesField:IExtraFieldConfigurationObject;
  private orderProcessField:IExtraFieldConfigurationObject;
  private processNonCatalogOrderTypeField:IExtraFieldConfigurationObject;
  private defaultB2bContactInfoField:IExtraFieldConfigurationObject;
  private saveWebshopSettingsLink:IExtraFieldConfigurationObject;

  constructor(
    private authService:AuthService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private ga1Service:GA1Service,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private lovService:LovService,
    private translationService:TranslationService,
    private urlService:UrlService,
    private webshopB2BSettingsDataService:WebshopB2BSettingsDataService,
    protected responsiveService:ResponsiveService,
    protected scrollService:ScrollService,
    protected stateService:GaStateService,
    protected reportFiltersUtil:ReportFiltersUtil
  ) {
    this.hasWritePermission = this.authService.viewWrite(SEC_WS_B2BSETTINGS);
  }

  public ngOnInit():void {
    this.loadData();
  }

  private loadData():void {
    this.loading = true;
    this.webshopB2BSettingsDataService.getWebshopB2BSettings(this.webshopId)
      .then((b2bSettingsData:IWebshopB2BSettingsData) => {
        this.model = b2bSettingsData;
        this.initSections();
        this.fillFields();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private initSections():void {
    this.formSections = [
      {
        key: 'ORDER_OVERVIEW',
        fields: this.createOrderOverviewFields(),
        clickable: false
      },
      {
        key: 'WEBSHOP_SETTINGS',
        fields: this.createWebshopSettingsFields()
      },
      {
        key: 'B2B_MESSAGES',
        isFullWidth: true,
        isCollapsed: false,
        clickable: false,
        expanded: {
          report: {
            id: 319,
            filters: {
              Webshop_id: this.webshopId
            },
            height: 200,
            gridOptions: {
              sideBar: false
            }
          }
        }
      },
      {
        key: 'B2B_FAQ',
        fields: this.createB2BFAQFields(),
        clickable: false
      },
      {
        key: 'EXTERNAL_PARTNER_RELATIONS',
        isFullWidth: true,
        isCollapsed: false,
        clickable: false,
        expanded: {
          report: {
            id: 313,
            browseListCode: 'EXTPREL',
            filters: {
              Webshop_id: this.webshopId,
              Relation_type_val: 'PROCUREMNT'
            },
            height: 200,
            gridOptions: {
              sideBar: false
            }
          }
        }
      }
    ];
  }

  private fillFields():void {
    if(this.priceSentInXmlFilesField) {
      this.formlyService.fillField(this.priceSentInXmlFilesField, this.lovService.list(LOV_PRICE_B2B_PO));
    }
    if(this.orderProcessField) {
      this.formlyService.fillField(this.orderProcessField, this.lovService.list(LOV_ORDER_PROCESS));
    }
    if(this.processNonCatalogOrderTypeField) {
      this.formlyService.fillField(this.processNonCatalogOrderTypeField, this.lovService.list(LOV_PROCESS_NON_CATALOG_ORDERS));
    }
  }

  private createOrderOverviewFields():ICollapsibleSectionFields {
    const fields:ICollapsibleSectionFields = {};

    fields.collapsed = [
      this.formlyService.aValue('numOrdersThisYear')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('numOrdersPreviousYear')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('revenueThisYear')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aValue('revenuePreviousYear')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build()
    ];

    return fields;
  }

  private createWebshopSettingsFields():ICollapsibleSectionFields {
    const fields:ICollapsibleSectionFields = {};

    this.defaultB2bContactInfoField = this.formlyService.aCustom()
      .withComponent(ListContactsComponent)
      .withLabelClass('d-none')
      .withValueClass('col-md-12')
      .withWrapperClass('col-md-12')
      .withHideExpression(() => !this.model.defaultB2bContact)
      .withExtraOptions({
        templateOptions: {
          inputs: {
            contacts: [ this.model.defaultB2bContact ]
          }
        }
      })
      .build();

    const readOnlyFields:IExtraFieldConfigurationObject[] = [
      this.formlyService.aValue('priceSentInXmlFiles')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withoutInfoOffset()
        .withEditable('b2bPurchaseOrderPrice')
        .build(),

      this.formlyService.aGroup('defaultB2bContactInfo')
        .withLabelClass(this.labelClass)
        .withValueClass(`${this.valueClass} col-form-label`)
        .withGroupClass('no-margin-bottom')
        .withFields([
          this.defaultB2bContactInfoField
        ])
        .withoutInfoOffset()
        .withEditable('defaultB2bContact')
        .build(),

      this.formlyService.aValue('orderProcess')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('emailOrders')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatBoolean)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('emailOrdersCharge')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withoutInfoOffset()
        .withFormat((emailOrdersCharge:number) => this.formattingService.formatPct(emailOrdersCharge, true))
        .build(),

      this.formlyService.aValue('processNonCatalogOrderType')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('cxmlShowTaxDetailInSummary')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatBoolean)
        .withoutInfoOffset()
        .build()
    ];

    const writableFields:IExtraFieldConfigurationObject[] = [
      this.priceSentInXmlFilesField = this.formlyService.aSelect('priceSentInXmlFiles')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withEditable('b2bPurchaseOrderPrice')
        .withoutInfoOffset()
        .build(),

      this.formlyService.aSearch('defaultB2bContact')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatContact)
        .withoutInfoOffset()
        .withExtraOptions({
          templateOptions: {
            searchFunction: {
              request: (filter:string) => this.webshopB2BSettingsDataService.getB2BSettingsContacts(this.webshopId, filter)
            },
            minSearchLength: 2
          }
        })
        .onChange((contact:IContact) => {
          this.defaultB2bContactInfoField.templateOptions.inputs.contacts = contact ? [ contact ] : undefined;
        })
        .build(),

      this.formlyService.aGroup('defaultB2bContactInfo')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFields([
          this.defaultB2bContactInfoField
        ])
        .build(),

      this.orderProcessField = this.formlyService.aSelect('orderProcess')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('emailOrders')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatBoolean)
        .withoutInfoOffset()
        .build(),

      this.formlyService.aValue('emailOrdersCharge')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat((emailOrdersCharge:number) => this.formattingService.formatPct(emailOrdersCharge, true))
        .withoutInfoOffset()
        .build(),

      this.processNonCatalogOrderTypeField = this.formlyService.aSelect('processNonCatalogOrderType')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.formlyService.aCheckbox('cxmlShowTaxDetailInSummary')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.saveWebshopSettingsLink = this.formlyService.aLink(this.translationService.instant('GA.BUTTONS.SAVE_LABEL', { label: 'WEBSHOP.B2B_SETTINGS.WEBSHOP_SETTINGS.TITLE' }), 'saveWebshopSettingsLink')
        .withoutWrapper()
        .onClick(this.saveWebshopSettings)
        .withExtraOptions({
          templateOptions: {
            linkClass: 'btn-success'
          }
        })
        .withHideExpression(() => !this.hasWritePermission)
        .build()
    ];

    fields.collapsed = readOnlyFields;
    fields.expanded = this.hasWritePermission ? writableFields : readOnlyFields;

    return fields;
  }

  private createB2BFAQFields():ICollapsibleSectionFields {
    const fields:ICollapsibleSectionFields = {};

    fields.collapsed = [
      this.formlyService.aCustom()
        .withoutWrapper()
        .withComponent(FaqLinksComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              faqCodes: [ FAQ_CUSTOMER_B2B_FORM, FAQ_B2B_CAPABILITIES, FAQ_B2B_BROKER_SSO, FAQ_ARIBA_AGREEMENT ]
            }
          }
        })
        .build()
    ];

    return fields;
  }

  public saveWebshopSettings = ():void => {
    this.saveWebshopSettingsLink.templateOptions.busy = true;
    this.webshopB2BSettingsDataService.saveWebshopB2BSettings(this.webshopId, this.model)
      .then((response:IWebshopB2BSettingsData) => {
        this.model = response;
        this.messageService.showFeedbackAfterSave('WEBSHOP.B2B_SETTINGS.WEBSHOP_SETTINGS.TITLE');
      })
      .finally(() => {
        this.saveWebshopSettingsLink.templateOptions.busy = false;
      });
  };
}